// Types
import { Employee } from '@/types/employee';
import { ActivityType } from '@shared/types';

// Constants
import { ROUTES } from '@shared/constants';

export enum TimeOffResponseStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WAITING_FOR_APPROVE = 'waiting_for_approve',
}

export enum EmployeeRelationshipRole {
  MENTOR = 'mentor',
  SUPPORTER = 'supporter',
  SUPPORTEE = 'supportee',
  MENTEE = 'mentee',
}

export enum EmployeeTrainingStatus {
  IDLE = 'idle',
  IN_PROGRESS = 'in-progress',
  PAUSED = 'paused',
  RESUMED = 'resumed',
  COMPLETED = 'completed',
  ARCHIVED = 'archived',
}

// Training field ID type enum for edit tracking
export enum TrainingFieldIdType {
  DESCRIPTION = 'description',
  PRESENTATION = 'presentation',
  FEEDBACK = 'feedback',
  CREATE_FEEDBACK = 'create-feedback',
  CREATE_PRESENTATION = 'create-presentation',
}

// Training status variant for Badge
export const EMPLOYEE_TRAINING_STATUS_VARIANT = {
  [EmployeeTrainingStatus.IDLE]: 'waiting',
  [EmployeeTrainingStatus.IN_PROGRESS]: 'active',
  [EmployeeTrainingStatus.PAUSED]: 'tertiary',
  [EmployeeTrainingStatus.RESUMED]: 'waiting',
  [EmployeeTrainingStatus.COMPLETED]: 'complete',
  [EmployeeTrainingStatus.ARCHIVED]: 'archived',
};

// Training status label for Badge
export const EMPLOYEE_TRAINING_STATUS_LABEL = {
  [EmployeeTrainingStatus.IDLE]: 'Not Started',
  [EmployeeTrainingStatus.PAUSED]: 'Pending',
  [EmployeeTrainingStatus.IN_PROGRESS]: 'In Progress',
  [EmployeeTrainingStatus.RESUMED]: 'Resumed',
  [EmployeeTrainingStatus.COMPLETED]: 'Completed',
  [EmployeeTrainingStatus.ARCHIVED]: 'Archived',
};

export enum EmployeeActivityGroup {
  TRAINING = 'TRAINING',
  PROJECT = 'PROJECT',
  REQUEST = 'REQUEST',
  USER = 'USER',
}

export const EMPLOYEE_ACTIVITY_GROUPS: Partial<
  Record<ActivityType, EmployeeActivityGroup>
> = {
  [ActivityType.TRAINING_CREATE]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_START]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_COMPLETE]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_PAUSE]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_RESUME]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_ARCHIVE]: EmployeeActivityGroup.TRAINING,
  [ActivityType.TRAINING_UNARCHIVE]: EmployeeActivityGroup.TRAINING,

  [ActivityType.PROJECT_MEMBER_ROLE_CHANGE]: EmployeeActivityGroup.PROJECT,
  [ActivityType.PROJECT_MEMBER_JOIN]: EmployeeActivityGroup.PROJECT,
  [ActivityType.PROJECT_MEMBER_LEAVE]: EmployeeActivityGroup.PROJECT,
  [ActivityType.PROJECT_MEMBER_RESUME]: EmployeeActivityGroup.PROJECT,

  [ActivityType.TICKET_REALLOCATION]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TICKET_ROOM_BOOKING]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TICKET_TIME_OFF]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TICKET_ON_BEHALF_TIME_OFF]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TICKET_MAINTENANCE]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TICKET_LUNCH_REGISTRATION]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TRAINING_FEEDBACK_RECEIVED]: EmployeeActivityGroup.REQUEST,
  [ActivityType.TRAINING_FEEDBACK_PRESENTATION]: EmployeeActivityGroup.REQUEST,

  [ActivityType.EMPLOYEE_ASSIGN_MENTOR]: EmployeeActivityGroup.USER,
  [ActivityType.EMPLOYEE_ASSIGN_MENTEE]: EmployeeActivityGroup.USER,
  [ActivityType.EMPLOYEE_UNASSIGN_MENTEE]: EmployeeActivityGroup.USER,
  [ActivityType.EMPLOYEE_UNASSIGN_MENTOR]: EmployeeActivityGroup.USER,
  [ActivityType.TRAINING_ASSIGN_SUPPORTER]: EmployeeActivityGroup.USER,
  [ActivityType.TRAINING_ASSIGN_TRAINEE]: EmployeeActivityGroup.USER,
} as const;

export const TRAINING_STATUS_LABELS = {
  [ActivityType.TRAINING_CREATE]: 'Created',
  [ActivityType.TRAINING_START]: 'Started',
  [ActivityType.TRAINING_COMPLETE]: 'Completed',
  [ActivityType.TRAINING_PAUSE]: 'Paused',
  [ActivityType.TRAINING_RESUME]: 'Resumed',
  [ActivityType.TRAINING_ARCHIVE]: 'Archived',
  [ActivityType.TRAINING_UNARCHIVE]: 'Unarchived',
} as const;

// Training activity timeline messages
export const TRAINING_ACTIVITY_MESSAGES: Partial<Record<ActivityType, string>> =
  {
    [ActivityType.TRAINING_CREATE]: 'created this training',
    [ActivityType.TRAINING_START]: 'marked the training as started',
    [ActivityType.TRAINING_COMPLETE]: 'marked this training as completed',
    [ActivityType.TRAINING_PAUSE]: 'marked this training as paused',
    [ActivityType.TRAINING_RESUME]: 'marked this training as resumed',
    [ActivityType.TRAINING_UNARCHIVE]: 'unarchived this training',
    [ActivityType.TRAINING_UPDATE]: 'updated training information',
    [ActivityType.TRAINING_FEEDBACK_RECEIVED]: 'has created feedback',
    [ActivityType.TRAINING_PRESENTATION]:
      'has created the plan for presentation',
  } as const;

export const EMPLOYEE_RELATIONSHIP_ROLE_LABEL = {
  [EmployeeRelationshipRole.MENTOR]: 'Mentor',
  [EmployeeRelationshipRole.SUPPORTER]: 'Supporter',
  [EmployeeRelationshipRole.SUPPORTEE]: 'Supportee',
  [EmployeeRelationshipRole.MENTEE]: 'Mentee',
} as const;

export const EMPLOYEE_RELATIONSHIP_ROLE_COLORS = {
  [EmployeeRelationshipRole.MENTOR]: 'border-l-golden', // #FDCD56 - Yellow
  [EmployeeRelationshipRole.MENTEE]: 'border-l-mint', // #67CCAB - Green
  [EmployeeRelationshipRole.SUPPORTER]: 'border-l-lavender', // #6C63A9 - Purple
  [EmployeeRelationshipRole.SUPPORTEE]: 'border-l-coral', // #E6766B - Orange/Coral
} as const;

export const EMPLOYEE_ACTIVITY_PAGE_STYLES = {
  timelineSection: {
    className: 'pt-0 lg:pt-0 bg-white',
    titleClassName: 'text-primary-700',
  },
  eventDate: {
    className: 'text-frost-900 leading-[18px]',
  },
  eventIcon: {
    className: 'border-primary-700',
    customColor: 'border-primary-700 border-1',
  },
  verticalConnector: {
    color: 'bg-primary-700',
  },
  eventTitle: {
    titleClassName: 'text-primary-700 md:leading-sm md:text-[28px]',
  },
} as const;

export const EMPLOYEE_DEFAULT_VALUE = {
  employee: {
    info: {
      name: '',
      groups: null,
      status: null,
      workingStatus: null,
      address: '',
      email: '',
      title: '',
      bio: '',
      avatar: '',
      gender: '',
      phone: '',
      username: '',
      birthday: '',
      room: '',
      onboardedAt: '',
    },
    // relationship: null,
    // trainings: null,
    // projects: null,
    // skills: null,
    // certs: null,
    // education: null,
    // timeOffRequests: null,
    // activities: null,
  } as unknown as Employee,
};

// Training activity types that should not display avatars
export const TRAINING_AVATAR_HIDDEN_TYPES = [
  ActivityType.TRAINING_ASSIGN_SUPPORTER,
  ActivityType.TRAINING_UNASSIGN_SUPPORTER,
] as const;

// Training activity types that display supporters grid
export const TRAINING_SUPPORTER_DISPLAY_TYPES = [
  ActivityType.TRAINING_ASSIGN_SUPPORTER,
  ActivityType.TRAINING_UNASSIGN_SUPPORTER,
] as const;

// Training statuses that should display a status badge
export const TRAINING_BADGE_DISPLAY_STATUSES = [
  EmployeeTrainingStatus.PAUSED,
  EmployeeTrainingStatus.IN_PROGRESS,
  EmployeeTrainingStatus.RESUMED,
] as const;

// Routes where participants use getEmployeeProfile instead of getEmployeeInfo (access restricted)
export const EMPLOYEE_INFO_RESTRICTED_ROUTES = [ROUTES.EMPLOYEE_TRAININGS];
